import { useEffect, useRef, useState } from "react";
import PortalLayout from "@/pages/portal/PortalLayout";
import api, { API } from "@/lib/api";
import { Send, Sparkles, Loader2, Plus } from "lucide-react";
import { toast } from "sonner";

const SUGGESTIONS = [
  "What is the MOQ for private label alkaline water?",
  "Walk me through the artwork approval process.",
  "How long does a CBD water launch take end-to-end?",
  "Which bottle sizes work best for hotel table water?",
];

const AIAssistant = () => {
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);
  const abortRef = useRef(null);

  const loadSessions = () => api.get("/ai/sessions").then((r) => setSessions(r.data.sessions)).catch(() => {});
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming]);

  const openSession = async (sid) => {
    setSessionId(sid);
    const { data } = await api.get(`/ai/sessions/${sid}`);
    setMessages(data.messages);
  };

  const newChat = () => { setSessionId(null); setMessages([]); };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    const userMsg = { role: "user", content: msg, id: `u-${Date.now()}` };
    const assistantId = `a-${Date.now()}`;
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "", id: assistantId, streaming: true }]);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;
    const token = localStorage.getItem("flowtalis_token");

    try {
      const resp = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,
          "Accept": "text/event-stream",
        },
        body: JSON.stringify({ session_id: sessionId, message: msg }),
        signal: controller.signal,
      });

      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events terminated by blank line
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const raw = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = raw.split("\n");
          let eventName = "message";
          let dataLine = "";
          for (const l of lines) {
            if (l.startsWith("event:")) eventName = l.slice(6).trim();
            else if (l.startsWith("data:")) dataLine += l.slice(5).trim();
          }
          if (!dataLine) continue;
          let payload = null;
          try { payload = JSON.parse(dataLine); } catch { continue; }

          if (eventName === "meta") {
            setSessionId(payload.session_id);
          } else if (eventName === "delta") {
            setMessages((m) => m.map((x) => x.id === assistantId ? { ...x, content: x.content + payload.text } : x));
          } else if (eventName === "done") {
            setMessages((m) => m.map((x) => x.id === assistantId ? { ...x, streaming: false } : x));
            loadSessions();
          } else if (eventName === "error") {
            toast.error(payload.detail || "AI error");
            setMessages((m) => m.filter((x) => x.id !== assistantId));
          }
        }
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        toast.error("AI is unavailable. Please try again.");
        setMessages((m) => m.filter((x) => x.id !== assistantId));
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  return (
    <PortalLayout>
      <div className="mb-6">
        <div className="ft-eyebrow mb-2">FLOWTALIS AI</div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
          <Sparkles style={{ color: "var(--ft-blue)" }} /> Enterprise Assistant
        </h1>
        <p className="text-[var(--ft-muted)] mt-2">Powered by Claude Sonnet 4.5 · Streaming live. Trained on FLOWTALIS processes, MOQs, and workflows.</p>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        <aside className="ft-card space-y-2 h-fit">
          <button onClick={newChat} data-testid="ai-new-chat" className="ft-btn-primary w-full justify-center">
            <Plus size={16} /> New Conversation
          </button>
          <div className="ft-eyebrow pt-4 pb-1">Recent</div>
          {sessions.length === 0 && <div className="text-sm text-[var(--ft-muted)]">No conversations yet.</div>}
          {sessions.map((s) => (
            <button
              key={s.session_id}
              onClick={() => openSession(s.session_id)}
              data-testid={`ai-session-${s.session_id}`}
              className={`w-full text-left ft-side-link ${sessionId === s.session_id ? "active" : ""}`}
            >
              <span className="truncate text-sm">{s.preview}</span>
            </button>
          ))}
        </aside>

        <div className="ft-card flex flex-col" style={{ minHeight: "560px" }}>
          <div className="flex-1 space-y-4 overflow-y-auto pr-2" data-testid="ai-chat-window">
            {messages.length === 0 && !streaming && (
              <div className="h-full flex flex-col items-center justify-center text-center py-10">
                <Sparkles size={32} style={{ color: "var(--ft-blue)" }} />
                <h3 className="mt-4 text-xl font-semibold">Ask FLOWTALIS anything.</h3>
                <p className="text-[var(--ft-muted)] mt-2 max-w-md">Product timelines, MOQs, launch strategy, compliance — get executive-level answers grounded in FLOWTALIS operations.</p>
                <div className="mt-8 grid gap-2 w-full max-w-lg">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`ai-suggestion-${s.slice(0,10)}`}
                      className="text-left px-4 py-3 rounded-xl border border-[var(--ft-border)] hover:border-[var(--ft-blue)] text-sm transition-colors"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] p-4 rounded-2xl whitespace-pre-wrap text-sm leading-relaxed ${m.role === "user" ? "bg-[var(--ft-blue)] text-white" : "bg-[var(--ft-soft-gray)] border border-[var(--ft-border)]"}`}>
                  {m.content}
                  {m.streaming && <span className="inline-block w-2 h-4 ml-0.5 align-middle animate-pulse" style={{ background: "var(--ft-blue)" }} />}
                </div>
              </div>
            ))}
            {streaming && messages[messages.length - 1]?.role === "user" && (
              <div className="flex justify-start">
                <div className="p-4 rounded-2xl bg-[var(--ft-soft-gray)] border border-[var(--ft-border)] flex items-center gap-2 text-sm">
                  <Loader2 size={16} className="animate-spin" style={{ color: "var(--ft-blue)" }} /> Thinking…
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-5 pt-5 border-t border-[var(--ft-border)] flex gap-2" data-testid="ai-form">
            <input
              className="ft-input flex-1"
              placeholder="Ask about MOQs, timelines, artwork, compliance…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={streaming}
              data-testid="ai-input"
            />
            <button type="submit" disabled={streaming || !input.trim()} className="ft-btn-primary" data-testid="ai-send">
              <Send size={16} />
            </button>
          </form>
        </div>
      </div>
    </PortalLayout>
  );
};

export default AIAssistant;
